import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '../utils/cn.js';
import { Loader2 } from 'lucide-react';
const buttonVariants = cva([
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'font-medium transition-all duration-150 ease-smooth',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    'select-none',
], {
    variants: {
        variant: {
            primary: [
                'bg-brand-600 text-white shadow-sm',
                'hover:bg-brand-700 active:bg-brand-800',
                'dark:bg-brand-500 dark:hover:bg-brand-400',
            ],
            secondary: [
                'bg-surface text-content border border-border shadow-sm',
                'hover:bg-surface-subtle hover:border-border-strong',
                'dark:bg-surface-raised dark:hover:bg-surface-overlay',
            ],
            ghost: [
                'text-content',
                'hover:bg-surface-subtle',
                'dark:hover:bg-surface-raised',
            ],
            danger: [
                'bg-red-600 text-white shadow-sm',
                'hover:bg-red-700 active:bg-red-800',
                'dark:bg-red-500 dark:hover:bg-red-600',
            ],
            link: [
                'text-brand-600 underline-offset-4',
                'hover:underline',
                'dark:text-brand-400',
                'h-auto px-0 py-0',
            ],
            outline: [
                'border border-border text-content bg-transparent shadow-sm',
                'hover:bg-surface-subtle hover:border-border-strong',
                'dark:hover:bg-surface-raised',
            ],
        },
        size: {
            xs: 'h-7 px-2.5 text-xs rounded',
            sm: 'h-8 px-3 text-sm rounded',
            md: 'h-9 px-4 text-sm rounded-md',
            lg: 'h-10 px-5 text-sm rounded-md',
            xl: 'h-11 px-6 text-base rounded-lg',
            icon: 'h-9 w-9 rounded-md',
            'icon-sm': 'h-8 w-8 rounded',
            'icon-lg': 'h-10 w-10 rounded-lg',
        },
    },
    defaultVariants: {
        variant: 'primary',
        size: 'md',
    },
});
const Button = React.forwardRef(({ className, variant, size, asChild = false, loading = false, leftIcon, rightIcon, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (_jsxs(Comp, { ref: ref, className: cn(buttonVariants({ variant, size, className })), disabled: disabled || loading, "aria-busy": loading, ...props, children: [loading ? (_jsx(Loader2, { className: "h-4 w-4 animate-spin", "aria-hidden": "true" })) : (leftIcon && _jsx("span", { "aria-hidden": "true", children: leftIcon })), children, !loading && rightIcon && _jsx("span", { "aria-hidden": "true", children: rightIcon })] }));
});
Button.displayName = 'Button';
export { Button, buttonVariants };
//# sourceMappingURL=Button.js.map